/**
 * Study Table Input Schema
 *
 * Combined Zod schema for internal study table query parameters.
 * This schema is used by tRPC endpoints for the /int/internal/studies page.
 */

import { z } from "zod";

import { filterGroupSchema } from "./filter-schemas";
import { sortConfigSchema } from "./sort-schemas";

// ============================================================================
// Sentinel Values
// ============================================================================

/**
 * Sentinel value used in `internalStudyTableInputSchema.internalOwnerIds` to
 * request studies with no internal owner. The repository query treats this
 * value specially (matches `internalOwnerId IS NULL`).
 *
 * Exported so client code and the repository share the exact same literal.
 * diverging strings would silently produce wrong results.
 */
export const UNASSIGNED_OWNER_FILTER_ID = "UNASSIGNED";

// ============================================================================
// Study Status Enum
// ============================================================================

export const studyStatusFilterSchema = z.enum([
  "DRAFT",
  "PRE_RECRUITING",
  "ACTIVE",
  "PAUSED",
  "FULFILLED",
  "STOPPED",
  "COMPLETED",
  "EXPIRED",
  "CANCELLED",
  "FAILED",
]);

export type StudyStatusFilter = z.infer<typeof studyStatusFilterSchema>;

// ============================================================================
// Internal Study Table Input Schema
// ============================================================================

/**
 * Input schema for internal (admin) study table queries.
 * Supports server-side filtering, sorting, pagination, and search.
 */
export const internalStudyTableInputSchema = z.object({
  // ========================================
  // Organization Filters
  // ========================================

  /**
   * Include only studies from these organizations (multi-select filter)
   */
  organizationIds: z.array(z.string().min(1)).optional(),

  /**
   * Exclude studies from these organizations
   * Used for filtering out test/demo orgs
   */
  excludeOrganizationIds: z.array(z.string().min(1)).optional(),

  // ========================================
  // Status Filters
  // ========================================

  /**
   * Filter by study statuses (multi-select)
   * Default: ["ACTIVE"] when not specified by client
   */
  statuses: z.array(studyStatusFilterSchema).optional(),

  // ========================================
  // Internal Owner Filter
  // ========================================

  /**
   * Filter by internal owner (Terac team member) user IDs (multi-select).
   * Pass {@link UNASSIGNED_OWNER_FILTER_ID} to include studies with no owner.
   */
  internalOwnerIds: z.array(z.string().min(1)).optional(),

  // ========================================
  // Advanced Filters (FilterGroup)
  // ========================================

  /**
   * Complex filter group with AND/OR logic for all filtering needs.
   * Supports filtering on: name, totalBudget, endingAt, createdAt,
   * targetResponseCount, interviewCount, etc.
   */
  filterGroup: filterGroupSchema.optional(),

  // ========================================
  // Search
  // ========================================

  /**
   * Full-text search across study name, organization name, project name
   */
  search: z.string().max(200).optional(),

  // ========================================
  // Sorting
  // ========================================

  /**
   * Sort configuration for dynamic sorting by any field.
   * Default: { field: "endingAt", order: "asc" } (soonest deadline first)
   */
  sortConfig: sortConfigSchema.optional(),

  // ========================================
  // Pagination
  // ========================================

  /**
   * Page number (1-indexed)
   */
  page: z.number().int().min(1).max(1000).default(1),

  /**
   * Items per page
   */
  limit: z.number().int().min(1).max(100).default(50),
});

export type InternalStudyTableInput = z.infer<
  typeof internalStudyTableInputSchema
>;

// ============================================================================
// Internal Study Table Response Types
// ============================================================================

/**
 * Pagination metadata in response
 */
export const studyPaginationResponseSchema = z.object({
  page: z.number(),
  limit: z.number(),
  totalCount: z.number(),
  totalPages: z.number(),
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean(),
});

export type StudyPaginationResponse = z.infer<
  typeof studyPaginationResponseSchema
>;

// ============================================================================
// Study Filter Field Definitions
// ============================================================================

/**
 * Available fields for advanced filtering in the study table.
 * Used by the FilterGroup component to build dynamic filters.
 */
export const studyFilterFieldDefinitions = [
  { id: "name", label: "Study Name", type: "text", category: "metadata" },
  {
    id: "status",
    label: "Status",
    type: "enum",
    category: "metadata",
    options: [
      { value: "DRAFT", label: "Draft" },
      { value: "PRE_RECRUITING", label: "Pre-Recruitment" },
      { value: "ACTIVE", label: "Active" },
      { value: "PAUSED", label: "Paused" },
      { value: "FULFILLED", label: "Fulfilled" },
      { value: "STOPPED", label: "Stopped" },
      { value: "COMPLETED", label: "Completed" },
      { value: "EXPIRED", label: "Expired" },
      { value: "CANCELLED", label: "Cancelled" },
      { value: "FAILED", label: "Failed" },
    ],
  },
  {
    id: "organizationId",
    label: "Organization",
    type: "enum",
    category: "metadata",
  },
  {
    id: "totalBudget",
    label: "Budget",
    type: "numeric",
    category: "metadata",
  },
  {
    id: "endingAt",
    label: "Deadline",
    type: "date",
    category: "metadata",
  },
  {
    id: "interviewCount",
    label: "Interviews Filled",
    type: "numeric",
    category: "metadata",
  },
  {
    id: "targetResponseCount",
    label: "Target Interviews",
    type: "numeric",
    category: "metadata",
  },
  {
    id: "createdAt",
    label: "Created Date",
    type: "date",
    category: "metadata",
  },
  {
    id: "launchedAt",
    label: "Launched Date",
    type: "date",
    category: "metadata",
  },
] as const;

export type StudyFilterFieldId =
  (typeof studyFilterFieldDefinitions)[number]["id"];
